/**
=========================================================
* Edushala - Premium Support Ticket Management
=========================================================
* Enhanced with reusable components and premium UI/UX
*/

import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Fade,
  Zoom,
} from "@mui/material";
import {
  ConfirmationNumber,
  Description,
  Person,
  Phone,
  PriorityHigh,
  AssignmentInd,
  Schedule,
  Update,
  Send,
  Search,
  FilterList,
  ChatBubbleOutline,
} from "@mui/icons-material";

// Layout Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Custom Components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import PaginationBar from "components/PaginationBar/PaginationBar";

// Premium Reusable Components
import PremiumCard from "components/PremiumCard/PremiumCard";
import PremiumDialog from "components/PremiumDialog/PremiumDialog";
import StatusBadge from "components/StatusBadge/StatusBadge";
import InfoRow from "components/InfoRow/InfoRow";
import MessageBubble from "components/MessageBubble/MessageBubble";
import PremiumSelect from "components/PremiumSelect/PremiumSelect";

// Data and Utils
import supportTicketTableData from "Views/Activities/SupportTicket/data/supportTicketTableData";
import axios, { baseURL } from "utils/api";
import { toast } from "react-toastify";

function SupportTicket() {
  // ==================== STATE MANAGEMENT ====================
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [showMyTickets, setShowMyTickets] = useState(false);

  // Dialog States
  const [viewDialog, setViewDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [messageDialog, setMessageDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Message States
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Admin Assignment States
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // ==================== API CALLS ====================
  const fetchTickets = async () => {
    setLoading(true);
    try {
      let url = `${baseURL}/support-tickets?`;
      if (priority) url += `priority=${priority}&`;
      if (status) url += `status=${status}&`;

      const response = await axios.get(url);
      setTickets(response.data.data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Failed to fetch support tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${baseURL}/user`);
      const adminData = response.data.data?.users || response.data.data || [];
      setAdmins(Array.isArray(adminData) ? adminData : []);
    } catch (error) {
      console.error("Error fetching admins:", error);
      setAdmins([]);
      toast.error("Failed to fetch admin list");
    }
  };

  const fetchMessages = async (ticketId) => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${baseURL}/messages/ticket/${ticketId}`);
      setMessages(response.data.data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
      setMessages([]);
      toast.error("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.warning("Please enter a message");
      return;
    }

    setSendingMessage(true);
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) {
        toast.error("User not found. Please login again.");
        return;
      }

      const user = JSON.parse(userStr);
      await axios.post(`${baseURL}/messages`, {
        message: newMessage,
        from: "ADMIN",
        userId: user._id,
        ticketId: selectedTicket._id,
      });

      setNewMessage("");
      await fetchMessages(selectedTicket._id);
      toast.success("Message sent successfully");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.message || "Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSaveStatus = async (e) => {
    e.preventDefault();
    setUpdatingStatus(true);
    try {
      const updateData = { status: selectedStatus };
      if (selectedAdmin) updateData.assignedTo = selectedAdmin;

      await axios.put(`${baseURL}/support-tickets/${selectedTicket._id}`, updateData);
      toast.success("Ticket updated successfully");
      setStatusDialog(false);
      fetchTickets();
    } catch (error) {
      console.error("Error updating ticket:", error);
      toast.error(error.response?.data?.message || "Failed to update ticket");
    } finally {
      setUpdatingStatus(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchAdmins();
  }, [priority, status]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // ==================== HANDLERS ====================
  const handleViewClick = (ticket) => {
    setSelectedTicket(ticket);
    setViewDialog(true);
  };

  const handleStatusClick = (ticket) => {
    setSelectedTicket(ticket);
    setSelectedStatus(ticket.status);
    setSelectedAdmin(ticket.assignedTo?._id || "");
    setStatusDialog(true);
  };

  const handleMessageClick = async (ticket) => {
    setSelectedTicket(ticket);
    setMessageDialog(true);
    await fetchMessages(ticket._id);
  };

  // Filter and Pagination
  const filteredTickets = showMyTickets
    ? tickets.filter((ticket) => {
        const userStr = localStorage.getItem("user");
        if (!userStr) return false;
        const user = JSON.parse(userStr);
        return ticket.assignedTo?._id === user._id;
      })
    : tickets;

  // Additional client-side filtering for search (by subject and parent name)
  const searchFilteredTickets = search
    ? filteredTickets.filter((ticket) => {
        const searchLower = search.toLowerCase();

        // Search in subject
        const subject = ticket.subject || "";
        if (subject.toLowerCase().includes(searchLower)) return true;

        // Search in parent/tutor name
        const userName =
          ticket.createdBy === "PARENT"
            ? ticket.parentId?.fullName || ""
            : ticket.tutorId?.fullName || "";
        if (userName.toLowerCase().includes(searchLower)) return true;

        // Search in parent/tutor email
        const userEmail =
          ticket.createdBy === "PARENT"
            ? ticket.parentId?.email || ""
            : ticket.tutorId?.email || "";
        if (userEmail.toLowerCase().includes(searchLower)) return true;

        return false;
      })
    : filteredTickets;

  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedTickets = searchFilteredTickets.slice(startIndex, startIndex + rowsPerPage);

  const { columns, rows } = supportTicketTableData(
    handleViewClick,
    handleStatusClick,
    handleMessageClick,
    paginatedTickets
  );

  // ==================== RENDER ====================

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <Fade in timeout={800}>
          <Card
            sx={{
              borderRadius: "20px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
              overflow: "visible",
            }}
          >
            {/* Header Section */}
            <ArgonBox
              p={4}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "20px 20px 0 0",
              }}
            >
              <ArgonBox display="flex" alignItems="center" gap={2} mb={2}>
                <ArgonBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <ConfirmationNumber sx={{ fontSize: 32, color: "#fff" }} />
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography
                    variant="h4"
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      textShadow: "2px 2px 4px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    Support Ticket Management
                  </ArgonTypography>
                  <ArgonTypography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      mt: 0.5,
                    }}
                  >
                    Manage and track all support tickets efficiently
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {/* Filters Section */}
            <ArgonBox p={3}>
              <PremiumCard elevated sx={{ p: 3 }}>
                <ArgonBox display="flex" alignItems="center" gap={1} mb={3}>
                  <FilterList sx={{ color: "#667eea" }} />
                  <ArgonTypography variant="h6" fontWeight="bold" color="#334155">
                    Filters & Search
                  </ArgonTypography>
                </ArgonBox>

                <Grid container spacing={3} alignItems="center">
                  {/* Priority Filter */}
                  <Grid item xs={12} sm={6} md={2.5}>
                    <PremiumSelect
                      label="Priority"
                      value={priority}
                      options={["", "Low", "Medium", "High"]}
                      onChange={(e) => setPriority(e.target.value)}
                      placeholder="ALL"
                    />
                  </Grid>

                  {/* Status Filter */}
                  <Grid item xs={12} sm={6} md={2.5}>
                    <PremiumSelect
                      label="Status"
                      value={status}
                      options={["", "OPEN", "CLOSED", "ON_HOLD", "TERMINATED"]}
                      onChange={(e) => setStatus(e.target.value)}
                      placeholder="ALL"
                    />
                  </Grid>

                  {/* My Tickets Checkbox */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showMyTickets}
                          onChange={(e) => {
                            setShowMyTickets(e.target.checked);
                            setCurrentPage(1);
                          }}
                          sx={{
                            color: "#667eea",
                            "&.Mui-checked": {
                              color: "#667eea",
                            },
                          }}
                        />
                      }
                      label={
                        <ArgonTypography variant="button" fontWeight="medium" color="#334155">
                          My Assigned Tickets
                        </ArgonTypography>
                      }
                    />
                  </Grid>

                  {/* Search */}
                  <Grid item xs={12} sm={6} md={4}>
                    <ArgonBox>
                      <ArgonInput
                        placeholder="Search by subject, parent/tutor name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        fullWidth
                      />
                    </ArgonBox>
                  </Grid>
                </Grid>
              </PremiumCard>
            </ArgonBox>

            {/* Table Section */}
            <ArgonBox px={3} pb={3}>
              {loading ? (
                <ArgonBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={8}
                >
                  <CircularProgress size={60} sx={{ color: "#667eea" }} />
                  <ArgonTypography variant="h6" color="#64748b" mt={3}>
                    Loading tickets...
                  </ArgonTypography>
                </ArgonBox>
              ) : searchFilteredTickets.length === 0 ? (
                <ArgonBox textAlign="center" py={8}>
                  <ConfirmationNumber sx={{ fontSize: 80, color: "#cbd5e1", mb: 2 }} />
                  <ArgonTypography variant="h5" color="#64748b" fontWeight="bold" mb={1}>
                    No Support Tickets Found
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#94a3b8">
                    Try adjusting the filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Zoom in timeout={600}>
                  <ArgonBox>
                    <PremiumCard>
                      <Table columns={columns} rows={rows} />
                    </PremiumCard>

                    {searchFilteredTickets.length > 0 && (
                      <ArgonBox mt={3}>
                        <PaginationBar
                          currentPage={currentPage}
                          rowsPerPage={rowsPerPage}
                          totalItems={searchFilteredTickets.length}
                          onPageChange={(e, page) => setCurrentPage(page)}
                          onRowsPerPageChange={(e) => {
                            setRowsPerPage(e.target.value);
                            setCurrentPage(1);
                          }}
                        />
                      </ArgonBox>
                    )}
                  </ArgonBox>
                </Zoom>
              )}
            </ArgonBox>
          </Card>
        </Fade>

        {/* ==================== VIEW TICKET DIALOG ==================== */}
        <PremiumDialog
          open={viewDialog}
          onClose={() => {
            setViewDialog(false);
            setSelectedTicket(null);
          }}
          title="Ticket Details"
          icon={ConfirmationNumber}
          maxWidth="md"
        >
          {selectedTicket && (
            <>
              {/* Main Ticket Info */}
              <PremiumCard gradient="purple" sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <InfoRow
                      icon={Description}
                      label="Subject"
                      value={selectedTicket.subject}
                      white
                    />
                  </Grid>

                  {selectedTicket.description && (
                    <Grid item xs={12}>
                      <InfoRow
                        icon={Description}
                        label="Description"
                        value={selectedTicket.description}
                        white
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <InfoRow
                      icon={Person}
                      label="Created By"
                      value={
                        selectedTicket.createdBy === "PARENT"
                          ? selectedTicket.parentId?.fullName || "Unknown Parent"
                          : selectedTicket.tutorId?.fullName || "Unknown Tutor"
                      }
                      white
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoRow
                      icon={Phone}
                      label="Contact"
                      value={
                        selectedTicket.parentId?.phone || selectedTicket.tutorId?.phone || "N/A"
                      }
                      white
                    />
                  </Grid>
                </Grid>
              </PremiumCard>

              {/* Status & Assignment Info */}
              <PremiumCard elevated sx={{ p: 3, mb: 3 }}>
                <ArgonTypography variant="h6" fontWeight="bold" mb={3} color="#334155">
                  Status & Assignment
                </ArgonTypography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <ArgonBox mb={1}>
                      <ArgonTypography variant="caption" color="#64748b" fontWeight="bold">
                        STATUS
                      </ArgonTypography>
                    </ArgonBox>
                    <StatusBadge status={selectedTicket.status} type="status" />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonBox mb={1}>
                      <ArgonTypography variant="caption" color="#64748b" fontWeight="bold">
                        PRIORITY
                      </ArgonTypography>
                    </ArgonBox>
                    <StatusBadge status={selectedTicket.priority} type="priority" />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <InfoRow
                      icon={AssignmentInd}
                      label="Assigned To"
                      value={selectedTicket.assignedTo?.fullName || "Not Assigned"}
                      iconColor="#10b981"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoRow
                      icon={Schedule}
                      label="Created At"
                      value={new Date(selectedTicket.createdAt).toLocaleString()}
                      iconColor="#8b5cf6"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoRow
                      icon={Update}
                      label="Updated At"
                      value={new Date(selectedTicket.updatedAt).toLocaleString()}
                      iconColor="#ef4444"
                    />
                  </Grid>
                </Grid>
              </PremiumCard>

              {/* Actions */}
              <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
                <ArgonButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setViewDialog(false);
                    setSelectedTicket(null);
                  }}
                >
                  Close
                </ArgonButton>
              </ArgonBox>
            </>
          )}
        </PremiumDialog>

        {/* ==================== UPDATE STATUS DIALOG ==================== */}
        <PremiumDialog
          open={statusDialog}
          onClose={() => {
            setStatusDialog(false);
            setSelectedTicket(null);
            setSelectedStatus("");
            setSelectedAdmin("");
          }}
          title="Update Ticket"
          icon={AssignmentInd}
          maxWidth="sm"
          headerGradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
        >
          <form onSubmit={handleSaveStatus}>
            <PremiumCard elevated sx={{ p: 3, mb: 3 }}>
              <PremiumSelect
                label="Status"
                value={selectedStatus}
                options={["OPEN", "CLOSED", "ON_HOLD", "TERMINATED"]}
                onChange={(e) => setSelectedStatus(e.target.value)}
                required
              />

              <ArgonBox>
                <ArgonTypography
                  variant="caption"
                  fontWeight="bold"
                  sx={{
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Assign to Admin
                </ArgonTypography>
                <select
                  value={selectedAdmin}
                  onChange={(e) => setSelectedAdmin(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    transition: "all 0.3s ease",
                  }}
                >
                  <option value="">Select Admin</option>
                  {Array.isArray(admins) && admins.length > 0 ? (
                    admins.map((admin) => (
                      <option key={admin._id} value={admin._id}>
                        {admin.fullName} - {admin.email}
                      </option>
                    ))
                  ) : (
                    <option disabled>No admins available</option>
                  )}
                </select>
              </ArgonBox>
            </PremiumCard>

            <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
              <ArgonButton
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setStatusDialog(false);
                  setSelectedTicket(null);
                }}
              >
                Cancel
              </ArgonButton>
              <ArgonButton
                variant="contained"
                color="success"
                type="submit"
                disabled={updatingStatus}
              >
                {updatingStatus ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
              </ArgonButton>
            </ArgonBox>
          </form>
        </PremiumDialog>

        {/* ==================== MESSAGE DIALOG ==================== */}
        <PremiumDialog
          open={messageDialog}
          onClose={() => {
            setMessageDialog(false);
            setSelectedTicket(null);
            setMessages([]);
            setNewMessage("");
          }}
          title={`Messages - ${selectedTicket?.subject || ""}`}
          icon={ChatBubbleOutline}
          maxWidth="md"
          headerGradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
        >
          {/* Messages Container */}
          <ArgonBox
            sx={{
              height: "400px",
              overflowY: "auto",
              p: 2,
              backgroundColor: "#f8fafc",
              borderRadius: "16px",
              mb: 3,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#e2e8f0",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#667eea",
                borderRadius: "10px",
                "&:hover": {
                  background: "#5568d3",
                },
              },
            }}
          >
            {loadingMessages ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress sx={{ color: "#667eea" }} />
              </ArgonBox>
            ) : messages.length > 0 ? (
              messages.map((msg, index) => (
                <MessageBubble
                  key={index}
                  message={{
                    senderName:
                      msg.from === "ADMIN"
                        ? msg.userId?.fullName || "Admin"
                        : msg.parentId?.fullName || msg.tutorId?.fullName || "Client",
                    text: msg.message,
                    timestamp: new Date(msg.createdAt).toLocaleString(),
                  }}
                  isAdmin={msg.from === "ADMIN"}
                />
              ))
            ) : (
              <ArgonBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <ChatBubbleOutline sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
                <ArgonTypography variant="h6" color="#64748b">
                  No messages yet
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#94a3b8">
                  Start the conversation!
                </ArgonTypography>
              </ArgonBox>
            )}
          </ArgonBox>

          {/* Message Input */}
          <PremiumCard elevated sx={{ p: 2 }}>
            <ArgonBox display="flex" gap={2}>
              <ArgonInput
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                multiline
                rows={2}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                }}
              />
              <ArgonButton
                variant="contained"
                color="info"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sendingMessage}
                sx={{
                  minWidth: "100px",
                  height: "fit-content",
                  alignSelf: "flex-end",
                }}
              >
                {sendingMessage ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <Send sx={{ mr: 1 }} />
                    Send
                  </>
                )}
              </ArgonButton>
            </ArgonBox>
          </PremiumCard>
        </PremiumDialog>
      </ArgonBox>

      <Footer />
    </DashboardLayout>
  );
}

export default SupportTicket;
